// Blueprint — map the business, then optimize ([[business-blueprint]]).
//
// Four maps (Systems, Data, Processes, People) as registry entities, drawn
// together on the Map tab; NoVA fills them by interview (map_business) and
// proposes Initiatives against them (suggest_optimizations); the Impact tab
// tracks forecast against realized. This is where a new customer starts —
// the printed Onboarding Guidebook and nventr.io/guide teach the same path.
import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import ModuleFrame, { Chip, useModuleTab } from '../../components/ModuleFrame';
import { createEntity, deleteEntity, updateEntity, useEntityList } from '../../data/entities';
import { useCan } from '../../data/permissions';
import { useAuth } from '../../AuthContext';
import { useDoc, useLiveConfig } from '../../data/firestore';
import { askNova } from '../../components/ConsoleHeader';
import { SPECS, RecordForm, usd } from '../../components/blueprint/fields';
import BlueprintMap from '../../components/blueprint/BlueprintMap';
import Initiatives from '../../components/blueprint/Initiatives';
import Impact from '../../components/blueprint/Impact';
import { mapGaps, blueprintStep, rollup } from '../../data/blueprintForecast';

const STEP_LABEL = { systems: 'Systems', data: 'Data', processes: 'Processes', people: 'People', optimize: 'Optimize', plan: 'Plan', build: 'Build', prove: 'Prove' };
const STEP_TAB = { systems: 'systems', data: 'data', processes: 'processes', people: 'people', optimize: 'initiatives', plan: 'initiatives', build: 'initiatives', prove: 'impact' };
const ORG_WIDE = { workspace: null };

export default function BlueprintModule() {
  const { org, myRole } = useOutletContext();
  const { user } = useAuth();
  const can = useCan(org, myRole);
  const canEdit = can('blueprint.update');
  const canAccept = can('blueprint.accept');
  const [tab, setTab] = useModuleTab('map');
  const [selected, setSelected] = useState({}); // tab → record id
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importNote, setImportNote] = useState('');

  const { docs: systems } = useEntityList('bizSystem', { org: org.id }, ORG_WIDE);
  const { docs: dataStores } = useEntityList('dataStore', { org: org.id }, ORG_WIDE);
  const { docs: processes } = useEntityList('bizProcess', { org: org.id }, ORG_WIDE);
  const { docs: units } = useEntityList('orgUnit', { org: org.id }, ORG_WIDE);
  const { docs: roles } = useEntityList('orgRole', { org: org.id }, ORG_WIDE);
  const { docs: initiatives } = useEntityList('initiative', { org: org.id }, ORG_WIDE);
  const { docs: integrations } = useEntityList('integration', { org: org.id });
  const { docs: members } = useEntityList('member', { org: org.id }, { orderBy: 'createdAt', dir: 'asc' });
  const { docs: apps } = useEntityList('app', { org: org.id });
  const { docs: agents } = useEntityList('agent', { org: org.id }, ORG_WIDE);
  const { docs: datasets } = useEntityList('dataset', { org: org.id }, ORG_WIDE);
  const { doc: summary } = useDoc(`orgs/${org.id}/blueprint/summary`);
  const { doc: config } = useLiveConfig();
  const opts = useMemo(() => ({ discountRate: config?.blueprint?.discountRate ?? 0.08, horizonYears: config?.blueprint?.horizonYears ?? 3 }), [config]);

  const map = useMemo(() => ({ systems: systems || [], dataStores: dataStores || [], processes: processes || [], units: units || [], roles: roles || [] }), [systems, dataStores, processes, units, roles]);
  const inits = useMemo(() => initiatives || [], [initiatives]);
  const loaded = systems && dataStores && processes && units && roles && initiatives;
  const refs = useMemo(() => ({ bizSystem: map.systems, dataStore: map.dataStores, bizProcess: map.processes, orgUnit: map.units, orgRole: map.roles, integration: integrations || [], dataset: datasets || [], app: apps || [], agent: agents || [], pipeline: [], trainingRun: [] }), [map, integrations, datasets, apps, agents]);
  const gaps = useMemo(() => (loaded ? mapGaps(map) : []), [loaded, map]);
  const step = loaded ? blueprintStep({ ...map, initiatives: inits }) : null;
  const roll = useMemo(() => rollup(inits, opts), [inits, opts]);
  const total = map.systems.length + map.dataStores.length + map.processes.length + map.units.length + map.roles.length;

  const kindOfTab = { systems: 'bizSystem', data: 'dataStore', processes: 'bizProcess', people: null };
  const scope = { org: org.id };
  // Map records are org-wide (workspaceId null) — the business has one map.
  const stamp = (p) => ({ workspaceId: null, ...p, createdBy: user.uid, createdAt: Date.now(), updatedAt: Date.now() });
  const save = async (entity, id, payload) => (id ? updateEntity(entity, scope, id, { ...payload, updatedAt: Date.now() }) : createEntity(entity, scope, stamp(payload)));

  const importExisting = async () => {
    setImporting(true); setImportNote('');
    let n = 0;
    try {
      for (const i of integrations || []) {
        if (map.systems.some((s) => s.integrationId === i.id || s.name === i.name)) continue;
        await createEntity('bizSystem', scope, stamp({ name: i.name, category: i.kind === 'knowledge' ? 'other' : 'custom', vendor: i.provider || '', integrationId: i.id, description: i.baseUrl || '', verdict: 'keep', painPoints: [] })); n += 1;
      }
      const byRole = {};
      for (const m of members || []) byRole[m.role] = (byRole[m.role] || 0) + 1;
      for (const [role, count] of Object.entries(byRole)) {
        const name = `${org.name} ${role}s`;
        if (map.roles.some((r) => r.name === name)) continue;
        await createEntity('orgRole', scope, stamp({ name, headcount: count, loadedAnnualCostUsd: 0, processIds: [], systemIds: [], augmentable: true, notes: 'Imported from console members — rename to the real job title.' })); n += 1;
      }
      setImportNote(n ? `Imported ${n} record${n > 1 ? 's' : ''} — rename and price them.` : 'Everything connected is already on the map.');
    } catch (e) { setImportNote(e.message); }
    setImporting(false);
  };

  const steps = ['systems', 'data', 'processes', 'people', 'optimize', 'plan', 'build', 'prove'];
  const stepIdx = steps.indexOf(step);
  const note = loaded
    ? `Blueprint, tab ${tab}: ${map.systems.length} systems, ${map.dataStores.length} data stores, ${map.processes.length} processes, ${map.units.length} units, ${map.roles.length} roles, ${inits.length} initiatives (${roll.byStatus.suggested || 0} suggested); journey step ${step}; forecast ${usd(roll.forecastAnnualUsd)}/yr, realized ${usd(roll.realizedUsd)}. NoVA: map_business, blueprint_status, suggest_optimizations, plan_initiative.`
    : 'Blueprint loading';

  return (
    <ModuleFrame
      title="Blueprint"
      subtitle="Map · Systems · Data · Processes · People · Initiatives · Impact"
      accent="#c084fc"
      note={note}
      help="Map the business first — the systems people live in, where the data is, how the work flows, who does it. Then nventr proposes what to optimize, builds it, and tracks the savings."
      context={(
        <>
          <Chip tone={total ? 'ok' : 'muted'}>{total} mapped</Chip>
          <Chip tone={roll.active ? 'ok' : 'muted'}>{usd(roll.forecastAnnualUsd)}/yr forecast</Chip>
          <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => askNova(total ? 'Continue mapping my business: what is still missing on the Blueprint, and ask me about it one question at a time.' : 'Map my business. Interview me one question at a time about our systems, data, processes and people, and fill in the Blueprint as we go.')}>◈ Map it with NoVA</button>
        </>
      )}
      steps={steps.map((s, i) => ({ id: s, label: STEP_LABEL[s], state: i < stepIdx ? 'done' : i === stepIdx ? 'current' : 'todo', to: `/orgs/${org.id}/blueprint?tab=${STEP_TAB[s]}` }))}
      tabs={[
        { id: 'map', label: 'Map' },
        { id: 'systems', label: 'Systems', count: map.systems.length },
        { id: 'data', label: 'Data', count: map.dataStores.length },
        { id: 'processes', label: 'Processes', count: map.processes.length },
        { id: 'people', label: 'People', count: map.units.length + map.roles.length },
        { id: 'initiatives', label: 'Initiatives', count: inits.length },
        { id: 'impact', label: 'Impact' },
      ]}
      tab={tab}
      onTab={(t) => { setTab(t); setAdding(false); }}
      verbs={[
        { label: 'Import what you already have', hint: `${(integrations || []).length} integrations · ${(members || []).length} members`, onClick: importExisting, disabled: !canEdit || importing },
        { label: 'Suggest initiatives', hint: 'NoVA reads the map', onClick: () => askNova('Read my Blueprint and suggest the initiatives with the best savings, one per lever where it makes sense.'), disabled: !canEdit || total < 3 },
        { label: 'The guidebook', hint: 'map, then optimize', to: '/guide' },
      ]}
    >
      {importNote && <p className="cs-help" style={{ fontSize: 12 }}>{importNote}</p>}
      {loaded && gaps.length > 0 && tab !== 'impact' && (
        <div className="bp-gaps" data-nova-note={`Map gaps: ${gaps.join(' ')}`}>
          <strong>Still missing</strong>
          <ul>{gaps.slice(0, 4).map((g) => <li key={g}>{g}</li>)}</ul>
        </div>
      )}

      {tab === 'map' && <BlueprintMap {...map} initiatives={inits} onPick={(kind, id) => { const t = { bizSystem: 'systems', dataStore: 'data', bizProcess: 'processes', orgUnit: 'people', orgRole: 'people' }[kind]; setSelected((s) => ({ ...s, [t]: id, peopleKind: kind })); setTab(t); }} />}

      {['systems', 'data', 'processes'].includes(tab) && (
        <RecordsTab spec={SPECS[kindOfTab[tab]]} docs={map[{ systems: 'systems', data: 'dataStores', processes: 'processes' }[tab]]} refs={refs} canEdit={canEdit} selectedId={selected[tab]} adding={adding} setAdding={setAdding}
          onSelect={(id) => setSelected((s) => ({ ...s, [tab]: id }))}
          onSave={(id, payload) => save(kindOfTab[tab], id, payload).then((r) => { setAdding(false); if (!id && r) setSelected((s) => ({ ...s, [tab]: r })); })}
          onDelete={(id) => deleteEntity(kindOfTab[tab], scope, id).then(() => setSelected((s) => ({ ...s, [tab]: null })))}
          summary={(d) => tab === 'systems' ? `${d.category || ''}${d.annualCostUsd ? ` · ${usd(d.annualCostUsd)}/yr` : ''} · ${d.verdict || 'undecided'}` : tab === 'data' ? `${d.kind || ''}${d.engine ? ` · ${d.engine}` : ''}${d.sizeGb ? ` · ${d.sizeGb} GB` : ''}` : `${(d.steps || []).length} steps · ${d.volumePerMonth || 0}/mo${(d.painPoints || []).length ? ` · ${(d.painPoints || []).length} pain points` : ''}`}
        />
      )}

      {tab === 'people' && (
        <ModuleFrame.Columns cols={2}>
          <RecordsTab spec={SPECS.orgUnit} docs={map.units} refs={refs} canEdit={canEdit} selectedId={selected.peopleKind === 'orgUnit' ? selected.people : null} adding={adding === 'orgUnit'} setAdding={(v) => setAdding(v ? 'orgUnit' : false)}
            onSelect={(id) => setSelected((s) => ({ ...s, people: id, peopleKind: 'orgUnit' }))}
            onSave={(id, payload) => save('orgUnit', id, payload).then(() => setAdding(false))}
            onDelete={(id) => deleteEntity('orgUnit', scope, id)}
            summary={(d) => `${d.headcount || 0} people${d.parentId ? ` · in ${map.units.find((u) => u.id === d.parentId)?.name || '…'}` : ''}`} compact />
          <RecordsTab spec={SPECS.orgRole} docs={map.roles} refs={refs} canEdit={canEdit} selectedId={selected.peopleKind === 'orgRole' ? selected.people : null} adding={adding === 'orgRole'} setAdding={(v) => setAdding(v ? 'orgRole' : false)}
            onSelect={(id) => setSelected((s) => ({ ...s, people: id, peopleKind: 'orgRole' }))}
            onSave={(id, payload) => save('orgRole', id, payload).then(() => setAdding(false))}
            onDelete={(id) => deleteEntity('orgRole', scope, id)}
            summary={(d) => `${d.headcount || 1} × ${d.loadedAnnualCostUsd ? usd(d.loadedAnnualCostUsd) : 'no rate'}${d.unitId ? ` · ${map.units.find((u) => u.id === d.unitId)?.name || ''}` : ''}`} compact />
        </ModuleFrame.Columns>
      )}

      {tab === 'initiatives' && (
        <Initiatives org={org} map={map} initiatives={inits} refs={refs} opts={opts} canEdit={canEdit} canAccept={canAccept} selectedId={selected.initiatives}
          onSelect={(id) => setSelected((s) => ({ ...s, initiatives: id }))}
          onCreate={(payload) => createEntity('initiative', scope, stamp({ ...payload, deliverables: [], milestones: [] })).then((r) => setSelected((s) => ({ ...s, initiatives: r || s.initiatives })))}
          onUpdate={(id, patch) => updateEntity('initiative', scope, id, { ...patch, ...(patch.acceptedBy === 'me' ? { acceptedBy: user.uid } : {}), updatedAt: Date.now() })}
          onDelete={(id) => deleteEntity('initiative', scope, id).then(() => setSelected((s) => ({ ...s, initiatives: null })))}
        />
      )}

      {tab === 'impact' && <Impact initiatives={inits} opts={opts} summary={summary} onPick={(id) => { setSelected((s) => ({ ...s, initiatives: id })); setTab('initiatives'); }} />}
    </ModuleFrame>
  );
}

function RecordsTab({ spec, docs, refs, canEdit, selectedId, adding, setAdding, onSelect, onSave, onDelete, summary, compact = false }) {
  const current = docs.find((d) => d.id === selectedId) || null;
  const list = (
    <ModuleFrame.Panel title={spec.plural} meta={`${docs.length} mapped`} actions={canEdit && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => { setAdding(true); onSelect(null); }}>+ {spec.label}</button>}>
      {!docs.length && !adding && <div className="cs-empty">No {spec.plural.toLowerCase()} yet{canEdit ? ` — add one, or tell NoVA about your ${spec.plural.toLowerCase()}.` : '.'}</div>}
      <ul className="mf-list">
        {docs.map((d) => (
          <li key={d.id}>
            <button type="button" className="mf-row" style={{ width: '100%', cursor: 'pointer', color: 'var(--text)', borderColor: current?.id === d.id ? `${spec.accent}99` : undefined }} onClick={() => { setAdding(false); onSelect(d.id); }}>
              <span className="bp-dot" style={{ background: spec.accent }} />
              <span className="mf-row-grow" style={{ textAlign: 'left' }}><strong>{d.name}</strong> <span className="mf-muted">{summary(d)}</span></span>
            </button>
          </li>
        ))}
      </ul>
    </ModuleFrame.Panel>
  );
  const editor = (adding || current) ? (
    <ModuleFrame.Panel title={adding ? `New ${spec.label.toLowerCase()}` : current.name} meta={adding ? '' : `id ${current.id}`} className={compact ? '' : 'mf-span-2'}>
      <RecordForm spec={spec} record={adding ? null : current} refs={refs} canEdit={canEdit} onSave={(payload) => onSave(adding ? null : current.id, payload)} onDelete={adding ? null : () => onDelete(current.id)} onCancel={adding ? () => setAdding(false) : null} />
    </ModuleFrame.Panel>
  ) : null;
  if (compact) return <div className="bp-stack">{list}{editor}</div>;
  return <ModuleFrame.Columns>{list}{editor || <ModuleFrame.Panel title={spec.label} meta="select a row" className="mf-span-2"><p className="cs-help">Pick a {spec.label.toLowerCase()} to edit it, or add one.</p></ModuleFrame.Panel>}</ModuleFrame.Columns>;
}
